import BannerBlockProps from "./BannerProps.interface";
import styles from "./Banner.module.scss";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import BoxWrap from "components/BoxWrap/BoxWrap";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getTextAlignment from "helpers/getTextAlignment";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import React, { FunctionComponent } from "react";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";

const Banner: FunctionComponent<BannerBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const bannerSizeClass = props.settings.bannerSize ? styles[`size_${props.settings.bannerSize}`] : styles.size_Medium;
    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    if (props.settings?.headlineAlignment) {
        wrapperStyles["--headline-alignment"] = getTextAlignment(props.settings?.headlineAlignment);
    }

    if (props.settings?.textAlignment) {
        wrapperStyles["--text-alignment"] = getTextAlignment(props.settings?.textAlignment);
    }

    if (props.settings?.textPosition) {
        wrapperClasses.push(styles[`position_${props.settings.textPosition}`]);
    }

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{...wrapperStyles, ...splitStyles}} id={identifier}>
            <div className={combineClasses(styles.container, bannerSizeClass, ...splitClasses, inViewClasses)} ref={inViewElementRef}>
                <div className={styles.content}>
                    {/* TODO: Refactor BoxWrap Component to useBoxWrap hook */}
                    <BoxWrap {...props?.settings?.boxWrap && props.settings.boxWrap?.[0]} className={styles.inner}>
                        {/* Tagline */}
                        {props.content.tagline ? (
                            <span className={styles.tagline}>{props.content.tagline}</span>
                        ) : null}

                        {/* Headline */}
                        {props.content.headline ? (
                            <DynamicHeading className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                        ) : null}

                        {/* Text */}
                        {props.content.bodyText ? (
                            <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                        ) : null}

                        {/* Buttons */}
                        {props.content.buttons && props.content.buttons.length > 0 ? (
                            <div className={styles.buttons}>
                                {props.content.buttons.map((button) => (
                                    <Button key={button.contentUdi} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                                ))}
                            </div>
                        ) : null}
                    </BoxWrap>
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default Banner;